/**
 * Prevent Rewardful from injecting duplicates of its tracking script.
 * Previous implementation lived in public/scripts/rewardful-guard.js;
 * importing it here keeps the logic in the main bundle so we avoid
 * an extra render-blocking request.
 */
const REWARDFUL_PATTERN = /r\.wdfl\.co\/rw\.js/;

function hasRewardfulScript(): boolean {
  return Array.prototype.some.call(
    document.scripts || [],
    (script: HTMLScriptElement) => REWARDFUL_PATTERN.test(script.src || "")
  );
}

function removeDuplicateRewardful(): void {
  let found = false;
  const scripts = Array.prototype.slice.call(document.scripts || []) as HTMLScriptElement[];
  scripts.forEach((script) => {
    if (!REWARDFUL_PATTERN.test(script.src || "")) return;
    if (!found) {
      found = true;
      return;
    }
    try {
      script.parentNode?.removeChild(script);
    } catch {
      /* no-op */
    }
  });
}

function guardAppendChild(target: Element | DocumentFragment | null): void {
  if (!target) return;
  const originalAppend = (target as any).appendChild as Element["appendChild"] | undefined;
  if (!originalAppend || (originalAppend as any).__rw_guarded__) return;

  const guardedAppend: Element["appendChild"] = function guarded(node: any) {
    try {
      if (node?.tagName === "SCRIPT") {
        const src = node.getAttribute("src") || "";
        if (REWARDFUL_PATTERN.test(src) && hasRewardfulScript()) {
          return node;
        }
      }
    } catch {
      // swallow and continue with original behaviour
    }
    return originalAppend.apply(this, arguments as any);
  };

  (guardedAppend as any).__rw_guarded__ = true;
  (target as any).appendChild = guardedAppend;
}

try {
  removeDuplicateRewardful();
  const head = document.head;
  const body = document.body;
  guardAppendChild(head);
  guardAppendChild(body);

  let debounceTimer: ReturnType<typeof setTimeout> | null = null;

  const scheduleCleanup = () => {
    if (debounceTimer) return;
    debounceTimer = setTimeout(() => {
      debounceTimer = null;
      removeDuplicateRewardful();
    }, 120);
  };

  const observer = new MutationObserver((records) => {
    for (const record of records) {
      if (record.type !== "childList") continue;
      const nodes = Array.from(record.addedNodes);
      if (nodes.some((node) => (node as HTMLElement)?.tagName === "SCRIPT")) {
        scheduleCleanup();
        return;
      }
    }
  });

  if (head) observer.observe(head, { childList: true });
  if (body) observer.observe(body, { childList: true, subtree: false });
} catch {
  // Silently ignore in environments without DOM support
}
